import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { SwapVert } from '@mui/icons-material'

import { Fields } from '../../components'
import { mutations, queries } from '../../graphql'
import { formatInvestmentAccountLabel } from '../../utils'
import { PortfolioSummary } from './portfolio-summary'

const GIVING_WALLET = 'GIVING'

// FLIGHT-604: shared dialog behind both "Request Liquidation" and "Request
// Investment Transfer". The donor picks both a From and a To among their own
// wallets -- the Giving Wallet or any of the fund's Investment Accounts --
// so it is always explicit which way the money moves. The two named actions
// only differ in their title and in which From/To they pre-select.
//
// From/To values are GIVING_WALLET or a portfolio id (as a string, matching
// MUI's select value handling). `null` in state means "not touched yet", so
// the pre-selection (and the sole-portfolio default below) can resolve once
// the portfolio list has loaded.
function InvestmentTransferDialogBase({ open, onClose, fundId, title, initialFrom, initialTo }) {
  const { data } = useQuery(queries.funds.myFundInvestmentPortfolios, { variables: { fundId }, skip: !open })
  const portfolios = data?.me?.fund?.investmentPortfolios || []

  const [from, setFrom] = useState(null)
  const [to, setTo] = useState(null)
  const [amount, setAmount] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Refetches every place this transfer can show up -- the portfolio summary
  // card (MyFundInvestmentPortfolios), a portfolio's own drill-down
  // (InvestmentPortfolioActivity), and the Giving Wallet activity table
  // (MyFundActivity) -- named by operation, not query+variables, so only
  // whichever is actually mounted/active refetches.
  const [initiateTransfer, { loading, error }] = useMutation(mutations.investmentTransfers.initiateInvestmentTransfer, {
    refetchQueries: ['MyFundInvestmentPortfolios', 'InvestmentPortfolioActivity', 'MyFundActivity'],
  })

  // With a single Investment Account there is nothing to choose between, so
  // an unset Investment side defaults to it.
  const soleInvestmentSide = portfolios.length === 1 ? String(portfolios[0].id) : ''
  const fromValue = from ?? initialFrom ?? (initialTo === GIVING_WALLET ? soleInvestmentSide : GIVING_WALLET)
  const toValue = to ?? initialTo ?? (initialFrom === GIVING_WALLET ? soleInvestmentSide : GIVING_WALLET)

  const endpointOptions = [
    { value: GIVING_WALLET, label: 'Giving Wallet' },
    ...portfolios.map((p) => ({
      value: String(p.id),
      label: formatInvestmentAccountLabel(p),
    })),
  ]
  const fromPortfolio = portfolios.find((p) => String(p.id) === fromValue)
  const toPortfolio = portfolios.find((p) => String(p.id) === toValue)

  const handleSwap = () => {
    setFrom(toValue)
    setTo(fromValue)
  }

  const handleClose = () => {
    setFrom(null)
    setTo(null)
    setAmount('')
    setSubmitted(false)
    onClose()
  }

  const endpointInput = (value) =>
    value === GIVING_WALLET ? { walletType: 'GIVING' } : { walletType: 'INVESTMENT', portfolioId: Number(value) }

  const handleSubmit = async () => {
    const fromEndpoint = endpointInput(fromValue)
    const toEndpoint = endpointInput(toValue)
    try {
      await initiateTransfer({
        variables: {
          input: {
            fundId,
            fromWalletType: fromEndpoint.walletType,
            fromPortfolioId: fromEndpoint.portfolioId,
            toWalletType: toEndpoint.walletType,
            toPortfolioId: toEndpoint.portfolioId,
            amount: Number(amount),
          },
        },
      })
      setSubmitted(true)
    } catch (e) {
      // error is already surfaced via the mutation's `error` state below
    }
  }

  const sameEndpoint = fromValue === toValue
  const canSubmit = !loading && fromValue && toValue && !sameEndpoint && amount && Number(amount) > 0

  // Money never moves directly between two Investment Accounts: once one side
  // is an Investment Account, the other side can only be the Giving Wallet.
  const endpointSelect = (label, value, onChange, otherValue) => (
    <TextField select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {endpointOptions.map((option) => {
        const investmentToInvestment = option.value !== GIVING_WALLET && otherValue && otherValue !== GIVING_WALLET
        return (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.value === otherValue || investmentToInvestment}
          >
            {option.label}
          </MenuItem>
        )
      })}
    </TextField>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {submitted ? (
          <Alert severity="success">Your transfer request has been submitted. Our team will review it shortly.</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error.message}</Alert>}
            {endpointSelect('From', fromValue, setFrom, toValue)}
            <Tooltip title="Swap From and To">
              <IconButton aria-label="Swap From and To" onClick={handleSwap} sx={{ alignSelf: 'center' }}>
                <SwapVert />
              </IconButton>
            </Tooltip>
            {endpointSelect('To', toValue, setTo, fromValue)}
            {fromPortfolio && <PortfolioSummary portfolio={fromPortfolio} evenColumns />}
            {toPortfolio && <PortfolioSummary portfolio={toPortfolio} evenColumns />}
            <TextField
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                inputComponent: Fields.CurrencyInput,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{submitted ? 'Close' : 'Cancel'}</Button>
        {!submitted && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// "Request Liquidation" -- launched from a portfolio's own card, so it opens
// with that Investment Account as From and the Giving Wallet as To.
export function RequestLiquidationDialog({ open, onClose, fundId, portfolio }) {
  if (!portfolio) return null
  return (
    <InvestmentTransferDialogBase
      open={open}
      onClose={onClose}
      fundId={fundId}
      title={`Request Liquidation -- ${portfolio.custodialNumber}`}
      initialFrom={String(portfolio.id)}
      initialTo={GIVING_WALLET}
    />
  )
}

// "Request Investment Transfer" -- launched from the top of the Giving Wallet
// table, so it opens with the Giving Wallet as From; the donor picks which
// Investment Account is To (pre-selected when they only have one).
export function RequestInvestmentTransferDialog({ open, onClose, fundId }) {
  return (
    <InvestmentTransferDialogBase
      open={open}
      onClose={onClose}
      fundId={fundId}
      title="Request Investment Transfer"
      initialFrom={GIVING_WALLET}
      initialTo={null}
    />
  )
}
